import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "./ui/Modal";
import { cx } from "./ui/cx";

/**
 * Cmd-K / Ctrl-K command palette.
 *
 *   <CommandPalette />
 *
 * Hotkeys:
 *   ⌘/Ctrl K        — open
 *   ↑ ↓             — navigate
 *   Enter           — run
 *   Esc             — close
 *
 * No external dependency (cmdk, kbar). Roughly 120 LOC of focused, accessible
 * code. Each command has a label, optional Marathi label, target route,
 * keywords (for fuzzy match), and an optional `keywordsMr` for Marathi typing.
 */

interface Command {
  id: string;
  label: string;
  labelMr?: string;
  to: string;
  keywords?: string[];
  section?: string;
  icon?: string;
}

const COMMANDS: Command[] = [
  // Navigation
  { id: "go.dashboard",   label: "Dashboard",            labelMr: "डॅशबोर्ड",         to: "/dashboard",        section: "Go", icon: "📊", keywords: ["home"] },
  { id: "go.my-cases",    label: "My Cases",             labelMr: "माझे प्रकरण",       to: "/my-cases",         section: "Go", icon: "📁" },
  { id: "go.firs",        label: "FIRs",                 labelMr: "एफआयआर",          to: "/firs",             section: "Go", icon: "📄" },
  { id: "go.complaints",  label: "Complaints",           labelMr: "तक्रारी",           to: "/complaints",       section: "Go", icon: "🗣" },
  { id: "go.evidence",    label: "Evidence",             labelMr: "पुरावे",            to: "/malkhana",         section: "Go", icon: "🗃" },
  { id: "go.cs",          label: "Charge Sheets",        labelMr: "दोषारोपपत्रे",       to: "/charge-sheets",    section: "Go", icon: "⚖" },
  { id: "go.court",       label: "Court Filings",        labelMr: "न्यायालय फायलिंग",    to: "/court/filings",    section: "Go", icon: "🏛" },
  { id: "go.diary",       label: "Case Diary (§192)",    labelMr: "तपास डायरी",        to: "/case-diary",       section: "Go", icon: "📔" },
  { id: "go.sos",         label: "SOS Alerts",           labelMr: "एसओएस",            to: "/sos",              section: "Go", icon: "🚨" },
  // New
  { id: "new.fir",         label: "New FIR",            labelMr: "नवीन एफआयआर",       to: "/fir/new",          section: "Create", icon: "＋", keywords: ["draft", "register"] },
  { id: "new.complaint",   label: "New Complaint",      labelMr: "नवीन तक्रार",        to: "/complaints/new",   section: "Create", icon: "＋" },
  { id: "new.voice-comp",  label: "Voice Complaint",    labelMr: "आवाज तक्रार",       to: "/complaints/voice", section: "Create", icon: "🎙" },
  { id: "new.arrest",      label: "Record Arrest",      labelMr: "अटक नोंदवा",        to: "/arrests/new",      section: "Create", icon: "🚓" },
  { id: "new.panchanama",  label: "New Panchanama",     labelMr: "नवीन पंचनामा",       to: "/panchanamas",      section: "Create", icon: "📜" },
  // Admin / tools
  { id: "tool.search",     label: "Search",             labelMr: "शोधा",              to: "/search",           section: "Tools", icon: "🔍" },
  { id: "tool.serial",     label: "Serial offender search", labelMr: "सिरीयल",       to: "/serial-search",    section: "Tools", icon: "🔎" },
  { id: "tool.ai",         label: "About MahaLLM",      labelMr: "MahaLLM",          to: "/mahallm",          section: "Tools", icon: "🤖" },
  { id: "tool.help",       label: "Help",               labelMr: "मदत",               to: "/help",             section: "Tools", icon: "❓" },
  { id: "tool.settings",   label: "Settings",           labelMr: "सेटिंग्ज",            to: "/settings",         section: "Tools", icon: "⚙" },
];

function score(c: Command, q: string): number {
  const s = q.toLowerCase().trim();
  if (!s) return 0;
  const hay = [c.label, c.labelMr, c.section, ...(c.keywords ?? [])].filter(Boolean).join(" ").toLowerCase();
  if (hay.includes(s)) return 100 - hay.indexOf(s);
  // Simple subsequence match for typo tolerance
  let i = 0; for (const ch of hay) { if (ch === s[i]) i++; if (i >= s.length) return 50; }
  return 0;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  // Global hotkey
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const k = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && k === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Focus input on open
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else { setQ(""); setCursor(0); }
  }, [open]);

  const results = useMemo(() => {
    if (!q.trim()) return COMMANDS;
    return COMMANDS
      .map((c) => ({ c, s: score(c, q) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .map((x) => x.c);
  }, [q]);

  useEffect(() => { setCursor(0); }, [q]);

  function run(c: Command) {
    setOpen(false);
    navigate(c.to);
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(results.length - 1, c + 1)); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    if (e.key === "Enter" && results[cursor]) { e.preventDefault(); run(results[cursor]); }
  }

  // Group by section
  const sections = results.reduce<Record<string, Command[]>>((acc, c) => {
    const s = c.section ?? "Other";
    (acc[s] ??= []).push(c);
    return acc;
  }, {});

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      title="Quick actions"
      description="Type to filter · ↑↓ to navigate · Enter to go"
      size="md"
    >
      <div onKeyDown={onKey}>
        <input
          ref={inputRef}
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Type a page or action — 'fir', 'arrest', 'चार्जशीट'…"
          className="mb-2 h-11 w-full min-h-tap rounded-md border border-default bg-surface px-3 text-sm outline-none focus:shadow-focus focus:border-[var(--c-brand)]"
          aria-label="Search commands"
        />
        <div role="listbox" aria-label="Commands" className="max-h-[55vh] overflow-y-auto">
          {results.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-muted">No matches.</div>
          )}
          {Object.entries(sections).map(([section, cmds]) => (
            <div key={section} className="mb-2">
              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-subtle">
                {section}
              </div>
              {cmds.map((c) => {
                const idx = results.indexOf(c);
                const active = idx === cursor;
                return (
                  <button
                    key={c.id}
                    role="option"
                    aria-selected={active}
                    onClick={() => run(c)}
                    onMouseEnter={() => setCursor(idx)}
                    className={cx(
                      "flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm focus-ring tap",
                      active ? "bg-[var(--c-brand-soft)] text-[var(--c-brand-strong)]" : "text-default hover:bg-[var(--c-surface-muted)]"
                    )}
                  >
                    <span aria-hidden className="w-6 text-base">{c.icon ?? "›"}</span>
                    <span className="flex-1 truncate">{c.label}</span>
                    {c.labelMr && <span lang="mr" className="text-xs text-muted">{c.labelMr}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div className="mt-2 flex items-center justify-between gap-2 border-t border-default pt-2 text-[11px] text-subtle">
          <span><kbd className="rounded bg-[var(--c-surface-muted)] px-1.5 py-0.5">↑↓</kbd> navigate</span>
          <span><kbd className="rounded bg-[var(--c-surface-muted)] px-1.5 py-0.5">Enter</kbd> select</span>
          <span><kbd className="rounded bg-[var(--c-surface-muted)] px-1.5 py-0.5">Esc</kbd> close</span>
        </div>
      </div>
    </Modal>
  );
}
